import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import "./AddScopeModule.css";

const ScopeModule = () => {
  const [scopeModules, setScopeModules] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    fetchScopes();
    fetchModules();
    fetchScopeModules();
  }, []);

  const fetchScopes = async () => {
    try {
      const res = await axios.get(`${API_BASE}/scope-catalogs/`);
      setScopes(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching scope catalogs:", error);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/form-modules/`);
      setModules(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching form modules:", error);
    }
  };

  const fetchScopeModules = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE}/scope-modules/`);
      setScopeModules(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching scope modules:", error);
      Swal.fire("Error", "Failed to load scope modules", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This scope module link will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/scope-modules/${id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Scope module has been removed.",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchScopeModules();
    } catch (error) {
      console.error("Error deleting scope module:", error);

      Swal.fire(
        "Error",
        error?.response?.data?.message || "Failed to delete scope module",
        "error"
      );
    }
  };

  const handleEdit = (item) => {
    navigate(`/catalog/edit-scope-module/${item.id}`);
  };

  const scopeLabel = (scopeId) => {
    const match = scopes.find((s) => s.id === scopeId);
    return match ? match.scope_name : "-";
  };

  const moduleLabel = (moduleId) => {
    const match = modules.find((m) => m.id === moduleId);
    return match ? match.module_name : "-";
  };

  const filteredScopeModules = scopeModules.filter((item) => {
    const search = searchTerm.toLowerCase();

    return (
      scopeLabel(item.scope).toLowerCase().includes(search) ||
      moduleLabel(item.form_module).toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(
    filteredScopeModules.length / entriesPerPage
  );

  const startIndex = (currentPage - 1) * entriesPerPage;

  const paginatedScopeModules = filteredScopeModules.slice(
    startIndex,
    startIndex + entriesPerPage
  );

  const startEntry =
    filteredScopeModules.length === 0 ? 0 : startIndex + 1;

  const endEntry = Math.min(
    startIndex + entriesPerPage,
    filteredScopeModules.length
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(
        1,
        "...",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages
      );
    } else {
      pages.push(
        1,
        "...",
        currentPage - 1,
        currentPage,
        currentPage + 1,
        "...",
        totalPages
      );
    }

    return pages;
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="scope-module-table-page">
          <div className="scope-module-table-header">
            <div className="scope-module-table-header-left">
              <h2>
                
                Scope Modules Management
              </h2>

              <span className="scope-module-table-count">
                {scopeModules.length} scope modules
              </span>
            </div>

            <div className="scope-module-table-header-right">
              <div className="scope-module-table-search-wrapper">
                <FiSearch className="scope-module-table-search-icon" />

                <input
                  type="text"
                  placeholder="Search scope modules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="scope-module-table-search-input"
                />
              </div>

              <button
                className="scope-module-table-refresh-btn"
                onClick={fetchScopeModules}
                title="Refresh"
              >
                <FiRefreshCw />
              </button>

              <button
                className="scope-module-table-add-btn"
                onClick={() => navigate("/catalog/add-scope-module")}
              >
                <FiPlus />
                Add Scope Module
              </button>
            </div>
          </div>

          {loading ? (
            <div className="scope-module-table-loader-container">
              <div className="scope-module-table-loader">
                <div className="scope-module-table-spinner"></div>
                <p>Loading scope modules...</p>
              </div>
            </div>
          ) : (
            <div className="scope-module-table-container">
              <table className="scope-module-table">
                <thead>
                  <tr>
                    <th>Scope</th>
                    <th>Form Module</th>
                    <th>Sequence</th>
                    <th>Required</th>
                    <th>Effective From</th>
                    <th>Effective To</th>
                    <th className="scope-module-table-actions-header">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedScopeModules.length > 0 ? (
                    paginatedScopeModules.map((item) => (
                      <tr key={item.id}>
                        <td className="scope-module-table-name">
                          <div className="scope-module-table-name-cell">
                            <div className="scope-module-table-avatar">
                              {scopeLabel(item.scope)
                                ?.charAt(0)
                                .toUpperCase()}
                            </div>

                            <span>{scopeLabel(item.scope)}</span>
                          </div>
                        </td>

                        <td>{moduleLabel(item.form_module)}</td>

                        <td>{item.sequence}</td>

                        <td>
                          <span
                            className={`scope-module-table-status ${
                              item.is_required
                                ? "scope-module-table-active"
                                : "scope-module-table-inactive"
                            }`}
                          >
                            <span className="scope-module-table-status-dot"></span>
                            {item.is_required ? "Required" : "Optional"}
                          </span>
                        </td>

                        <td>{item.effective_from || "-"}</td>

                        <td>{item.effective_to || "-"}</td>

                        <td className="scope-module-table-actions-cell">
                          <button
                            className="scope-module-table-edit-btn"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            className="scope-module-table-delete-btn"
                            onClick={() => handleDelete(item.id)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="scope-module-table-no-data"
                      >
                        <div className="scope-module-table-empty-state">
                          <span className="scope-module-table-empty-icon">
                            📋
                          </span>

                          <p>No scope modules found</p>

                          <span className="scope-module-table-empty-sub">
                            {searchTerm
                              ? "Try adjusting your search"
                              : "Add your first scope module to get started"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {filteredScopeModules.length > 0 && (
                <div className="scope-module-table-pagination">
                  <div className="scope-module-table-pagination-info">
                    Showing <strong>{startEntry}</strong> to{" "}
                    <strong>{endEntry}</strong> of{" "}
                    <strong>{filteredScopeModules.length}</strong> entries
                  </div>

                  <div className="scope-module-table-pagination-controls">
                    <select
                      className="scope-module-table-entries-select"
                      value={entriesPerPage}
                      onChange={(e) =>
                        setEntriesPerPage(Number(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>

                    <button
                      className="scope-module-table-page-btn scope-module-table-prev-next"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      title="Previous"
                    >
                      <FiChevronLeft />
                    </button>

                    <div className="scope-module-table-page-numbers">
                      {getPageNumbers().map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`dots-${index}`}
                            className="scope-module-table-page-dots"
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={page}
                            className={`scope-module-table-page-btn ${
                              currentPage === page
                                ? "scope-module-table-page-active"
                                : ""
                            }`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      className="scope-module-table-page-btn scope-module-table-prev-next"
                      onClick={() =>
                        setCurrentPage((prev) =>
                          Math.min(prev + 1, totalPages)
                        )
                      }
                      disabled={
                        currentPage === totalPages || totalPages === 0
                      }
                      title="Next"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScopeModule;